import { Box, GlobalStyles, Typography, useTheme } from '@mui/material';
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useColorMode } from '../../hooks/useColorMode';
import { usePermissions } from '../../hooks/usePermissions';
import { isAiChatEnabled } from '../../services/aiChat';
import logoMark from '../../img/logo-branco.png';
import { KitButton } from '../common/MobileKit';
import { protoIcon } from '../common/ProtoIcon';
import MobileSheet from '../common/MobileSheet';
import MobileMoreHeader from './MobileMoreHeader';
import { AppBarActionButton, AppBarActionsProvider, AppBarSubtitleProvider, AppBarTitleProvider, appBarButtonSx } from './AppBarActions';
import MobileQuickActions, { MobileQuickActionsEdit, useLongPress, useQuickActions, useQuickPlusSelection } from './MobileQuickActions';
import ErrorBoundary from './ErrorBoundary';
import { isBottomNavVisible } from './bottomNavPaths';
import { useFlatNavItems } from './navConfig';

/**
 * Shell mobile — reproduz `prototipos-mobile/assets/mobile.css`/`mobile.js`:
 * barra superior clara (título 17px, ou título grande 28px nas telas raiz) e
 * barra inferior com Início / Ordens / (+) / Orçamentos / Mais. O botão (+) abre
 * a folha "O que você quer fazer?" (`MobileQuickActions`).
 *
 * Só é montado por `Main.js` quando `useIsMobileShell()` é verdadeiro. Reaproveita
 * as mesmas rotas e os mesmos gates de permissão do shell desktop — não cria
 * regra de negócio nova.
 */

// Barra inferior do protótipo (`.tabbar`): altura 64px (+ safe-area), grade 1fr 1fr 74px 1fr 1fr.
const TABBAR_HEIGHT = 64;

const Home = protoIcon('home');
const ClipboardList = protoIcon('orders');
const FileText = protoIcon('quote');
const LayoutGrid = protoIcon('grid');
const Plus = protoIcon('plus');
const ArrowLeft = protoIcon('back');
const Moon = protoIcon('moon');
const Sun = protoIcon('sun');
const Sparkles = protoIcon('sparkles');

// Destinos da barra inferior. `roots` = rotas que marcam o destino como ativo.
const TABS = [
    { id: 'home', label: 'Início', path: '/home', icon: Home, permission: 'home.view', roots: ['/home'] },
    { id: 'orders', label: 'Ordens', path: '/serviceorders', icon: ClipboardList, permission: 'serviceorder.view', roots: ['/serviceorder', '/billing/serviceorders'] },
    { id: 'quotes', label: 'Orçamentos', path: '/servicequotes', icon: FileText, permission: 'servicequote.view', roots: ['/servicequotes'] },
];

// Telas "raiz" (abas): sem botão voltar, título grande (`data-large` do protótipo).
const ROOT_PATHS = ['/home', '/serviceorders', '/servicequotes', '/more'];

// Rotas fora do menu lateral que precisam de título na barra superior.
const STATIC_TITLES = {
    '/home': 'Início',
    '/more': 'Mais',
    '/myaccount': 'Minha conta',
    '/plans': 'Planos',
    '/chat': 'Assistente',
    '/customers/new': 'Novo cliente',
    '/products/new': 'Novo produto',
    '/sales': 'Minhas vendas',
    '/dashboards/sale': 'Vendas',
    '/dashboards/order': 'Ordens de serviço',
    '/dashboards/vehicle': 'Veículos',
    '/dashboards/quote': 'Orçamentos',
    '/dashboards/customer': 'Clientes',
    '/dashboards/product': 'Produtos',
    '/serviceorders': 'Ordens',
    '/servicequotes': 'Orçamentos',
    '/servicequotes/generate': 'Novo orçamento',
    '/serviceorder/generate': 'Nova ordem',
    '/serviceorders/upcoming': 'Próximos serviços',
    '/billing/serviceorders': 'Faturamento de OS',
    '/purchases/new': 'Nova compra',
    '/inventory': 'Inventário',
    '/inventory/new': 'Novo inventário',
    '/purchases/import': 'Importar NF-e',
    '/categories': 'Categorias',
    '/services': 'Serviços',
    '/vehicles': 'Veículos',
    '/suppliers': 'Fornecedores',
    '/categories/new': 'Nova categoria',
    '/services/new': 'Novo serviço',
    '/vehicles/new': 'Novo veículo',
    '/suppliers/new': 'Novo fornecedor',
    '/vehicle_checklist': 'Checklists',
    '/vehicle_checklist/new': 'Novo checklist',
    '/checklist/groups': 'Grupos de inspeção',
    '/cashregisters': 'Caixas',
    '/cashregisters/new': 'Novo caixa',
    '/cardmachines': 'Maquininhas',
    '/cardmachines/new': 'Nova maquininha',
    '/transactions': 'Lançamentos',
    '/transactions/new': 'Novo lançamento',
    '/accounts/payable': 'Contas a pagar',
    '/accounts/receivable': 'Contas a receber',
    '/reports': 'Relatórios',
    '/reports/sales': 'Relatório de vendas',
    '/reports/serviceorder': 'Relatório de ordens',
    '/reports/products': 'Relatório de produtos',
    '/reports/stock': 'Relatório de estoque',
    '/reports/checklist': 'Relatório de checklists',
    '/reports/inventory': 'Relatório de inventário',
    '/reports/cash-flow': 'Fluxo de caixa',
    '/reports/customers': 'Relatório de clientes',
    '/reports/vehicles': 'Relatório de veículos',
    '/users/new': 'Novo usuário',
    '/profiles/new': 'Novo perfil',
};

// A barra inferior só aparece nas telas raiz de `bottomNavPaths.js` (Início,
// Ordens, Orçamentos e Mais). Nas demais ela é ocultada (formulários têm rodapé
// próprio em `bottom: 0`); a navegação segue pelo botão voltar do AppBar.

function matchesRoots(pathname, roots) {
    // '/sales' (Minhas Vendas) não pertence ao destino '/sale' — compara por
    // segmento, não por prefixo de string.
    return roots.some((root) => pathname === root || pathname.startsWith(`${root}/`) || (root === '/serviceorder' && pathname.startsWith('/serviceorders')));
}

function useScreenTitle() {
    const { pathname } = useLocation();
    const navItems = useFlatNavItems();

    if (STATIC_TITLES[pathname]) return STATIC_TITLES[pathname];

    const exact = navItems.find((item) => item.path === pathname);
    if (exact) return exact.label;

    const prefix = navItems
        .filter((item) => item.path !== '/' && pathname.startsWith(item.path))
        .sort((a, b) => b.path.length - a.path.length)[0];
    return prefix ? prefix.label : 'FlowVendas';
}

// Trava o zoom (pinch, double-tap) só enquanto o shell mobile está montado.
// O desktop/tablet continua com zoom livre (acessibilidade).
// Contador/original em módulo: MobileLayout e o PDV podem montar o hook juntos
// (e o StrictMode remonta); sem isso o 2º uso leria o valor já travado como "original".
const LOCKED_VIEWPORT = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';
let zoomLockCount = 0;
let zoomLockOriginal = null;

export function useLockZoom() {
    React.useEffect(() => {
        const meta = document.querySelector('meta[name="viewport"]');
        if (meta) {
            if (zoomLockCount === 0) zoomLockOriginal = meta.getAttribute('content');
            zoomLockCount += 1;
            meta.setAttribute('content', LOCKED_VIEWPORT);
        }
        // O foco num input na tela de login pode ter ampliado a página (iOS/Android);
        // desfocar e reaplicar a meta no próximo frame devolve a escala a 100% antes de travar.
        if (document.activeElement && document.activeElement !== document.body && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
        }
        const rafId = window.requestAnimationFrame(() => {
            if (meta) meta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover');
            window.requestAnimationFrame(() => meta && meta.setAttribute('content', LOCKED_VIEWPORT));
        });

        // iOS Safari ignora user-scalable=no: bloqueia o gesto de pinch na mão.
        const preventGesture = (e) => e.preventDefault();
        const preventPinch = (e) => {
            if (e.touches && e.touches.length > 1) e.preventDefault();
        };
        document.addEventListener('gesturestart', preventGesture);
        document.addEventListener('gesturechange', preventGesture);
        document.addEventListener('touchmove', preventPinch, { passive: false });

        return () => {
            window.cancelAnimationFrame(rafId);
            if (meta) {
                zoomLockCount -= 1;
                if (zoomLockCount === 0 && zoomLockOriginal !== null) meta.setAttribute('content', zoomLockOriginal);
            }
            document.removeEventListener('gesturestart', preventGesture);
            document.removeEventListener('gesturechange', preventGesture);
            document.removeEventListener('touchmove', preventPinch);
        };
    }, []);
}

// Seletor de aparência (`.opt-cards` / `.opt` do protótipo).
function ThemeOption({ active, icon: Icon, label, hint, onClick }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                border: '1.5px solid',
                borderColor: active ? 'primary.main' : 'divider',
                borderRadius: '14px',
                p: '12px',
                backgroundColor: active ? 'info.bg' : 'background.paper',
                color: active ? 'primary.main' : 'text.primary',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '6px',
                fontWeight: 700,
                fontSize: 13,
                lineHeight: 1.4,
                textAlign: 'left',
                fontFamily: 'inherit',
                cursor: 'pointer',
            }}
        >
            <Box component="span" sx={{ display: 'inline-flex', color: active ? 'primary.main' : 'text.disabled' }}><Icon size={20} /></Box>
            {label}
            <Box component="small" sx={{ fontWeight: 500, color: 'text.disabled', fontSize: 11.5 }}>{hint}</Box>
        </Box>
    );
}

// O protótipo carrega a Inter do Google Fonts; o tema já a lista primeiro em `fontFamily`,
// mas o app não a distribui. Carregamos só enquanto o shell mobile está montado (desktop inalterado).
const INTER_FONT_HREF = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';

export function useInterFont() {
    React.useEffect(() => {
        if (document.querySelector(`link[href="${INTER_FONT_HREF}"]`)) return undefined;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = INTER_FONT_HREF;
        document.head.appendChild(link);
        return () => { if (link.parentNode) link.parentNode.removeChild(link); };
    }, []);
}

export default function MobileLayout() {
    useLockZoom();
    useInterFont();
    const theme = useTheme();
    const navigate = useNavigate();
    const { pathname, state: locationState } = useLocation();
    const { hasPermission, userData } = usePermissions();
    const { mode, toggleMode } = useColorMode();
    const defaultTitle = useScreenTitle();
    const [titleOverride, setTitleOverride] = React.useState(null);
    const title = titleOverride || defaultTitle;
    const quickActions = useQuickActions();
    const quickPlus = useQuickPlusSelection(quickActions, userData?.id ? `fv:nav:plus:${userData.id}` : null);
    const [quickEditOpen, setQuickEditOpen] = React.useState(false);
    const quickPress = useLongPress(() => setQuickEditOpen(true));
    const [appBarAction, setAppBarAction] = React.useState(null);
    const [subtitle, setSubtitle] = React.useState(null);
    const [quickOpen, setQuickOpen] = React.useState(false);
    const [themeOpen, setThemeOpen] = React.useState(false);
    const [themeChoice, setThemeChoice] = React.useState(mode);

    const isRoot = ROOT_PATHS.includes(pathname);
    const hideBottomNav = !isBottomNavVisible(pathname);
    const tabs = TABS.filter((tab) => hasPermission(tab.permission));
    const activeTabId = tabs.find((tab) => matchesRoots(pathname, tab.roots))?.id;
    // "Mais" fica ativo em /more e em qualquer tela que não seja de uma aba.
    const moreActive = !activeTabId;
    const showAiAction = pathname === '/home' && isAiChatEnabled() && hasPermission('ai.use');

    // iOS standalone (PWA): ao trocar de rota o WebKit às vezes não repinta o conteúdo
    // (header/miolo em branco) até o primeiro gesto de rolagem. Um nudge de 1px no próximo
    // frame simula esse gesto e força a repintura.
    React.useEffect(() => {
        const id = window.requestAnimationFrame(() => {
            window.scrollTo(0, window.scrollY + 1);
            window.requestAnimationFrame(() => window.scrollTo(0, window.scrollY - 1));
        });
        return () => window.cancelAnimationFrame(id);
    }, [pathname]);

    const handleBack = () => {
        // Ordem criada/finalizada no fluxo (gerar → operar → visualizar): voltar vai à lista, não às telas do fluxo.
        if (locationState?.fromFlow && /^\/serviceorders?\/[^/]+\/(operator|view)$/.test(pathname)) {
            navigate('/serviceorders', { replace: true });
            return;
        }
        // idx > 0: há uma tela anterior dentro do app; senão volta ao início.
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);
        } else {
            navigate('/home');
        }
    };

    const openThemeSheet = () => {
        setThemeChoice(mode);
        setThemeOpen(true);
    };

    const applyTheme = () => {
        if (themeChoice !== mode) toggleMode();
        setThemeOpen(false);
    };

    const renderTab = ({ id, label, path, icon: Icon, active }) => (
        <Box
            key={id}
            component="button"
            type="button"
            onClick={() => { if (!active || pathname !== path) navigate(path); }}
            aria-label={label}
            aria-current={active ? 'page' : undefined}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '3px',
                minWidth: 0,
                p: '4px 0',
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                fontFamily: 'inherit',
                color: active ? 'primary.main' : 'text.disabled',
                fontSize: 10.5,
                fontWeight: 600,
                lineHeight: 1.4,
                WebkitTapHighlightColor: 'transparent',
            }}
        >
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    ...(active ? { backgroundColor: 'info.bg', borderRadius: '12px', p: '3px 14px', boxSizing: 'content-box' } : {}),
                }}
            >
                <Icon size={20} />
            </Box>
            <Box component="span" sx={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Box>
        </Box>
    );

    return (
        <Box
            data-mobile-shell
            sx={{ minHeight: '100vh', '@supports (height: 100dvh)': { minHeight: '100dvh' }, display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}
        >
            {/* Ajustes globais só enquanto o shell mobile está montado: alvos de toque
                >= 44px, inputs a 16px (iOS não dá zoom ao focar) e sem scroll horizontal. */}
            <GlobalStyles
                styles={{
                    // touch-action pan-x/pan-y: permite rolagem, bloqueia pinch e double-tap zoom.
                    'html, body': { overflowX: 'hidden', WebkitTextSizeAdjust: '100%', textSizeAdjust: '100%', touchAction: 'pan-x pan-y' },
                    // overflow-x: hidden em html E body transforma o body em contêiner de rolagem:
                    // no iOS (PWA) isso quebra o header sticky e a repintura ao voltar de uma tela
                    // (miolo/header em branco, só a barra fixa visível). `clip` corta sem criar contêiner.
                    '@supports (overflow-x: clip)': { 'html, body': { overflowX: 'clip' } },
                    // Oculta a barra de rolagem (a rolagem continua funcionando): visual mais limpo, estilo app nativo.
                    '*': { scrollbarWidth: 'none', msOverflowStyle: 'none' },
                    '*::-webkit-scrollbar': { display: 'none', width: 0, height: 0 },
                    // Título do page-head das telas (h1 marcado com data-page-title) repete o
                    // título do AppBar; no mobile fica só o subtítulo (e as ações ao lado).
                    'main [data-page-title]': { display: 'none' },
                    // Abas: texto em uma linha só, mais compacto (sem quebra).
                    '.MuiTab-root': { whiteSpace: 'nowrap', minWidth: 'auto', minHeight: 44, padding: '8px 14px', fontSize: 13, fontWeight: 600, textTransform: 'none', lineHeight: 1.2 },
                    '.MuiButton-root': { minHeight: 44 },
                    '.MuiIconButton-root': { minWidth: 44, minHeight: 44 },
                    '.MuiInputBase-input, .MuiSelect-select': { fontSize: '16px !important' },
                    '.MuiTableContainer-root': { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
                    '.MuiDialog-paper': { margin: 16, width: 'calc(100% - 32px)', maxWidth: 'calc(100% - 32px)' },
                    '.MuiDialog-paper.MuiDialog-paperFullScreen': { margin: 0, width: '100%', maxWidth: '100%' },
                }}
            />

            {/* Barra superior (`.appbar`; `.appbar.large` nas telas raiz) */}
            <Box
                component="header"
                sx={{
                    flexShrink: 0,
                    position: 'sticky',
                    top: 0,
                    transform: 'translateZ(0)',
                    WebkitBackfaceVisibility: 'hidden',
                    zIndex: theme.zIndex.appBar,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: isRoot ? 'stretch' : 'center',
                    flexDirection: isRoot ? 'column' : 'row',
                    gap: isRoot ? 0 : '6px',
                    p: isRoot
                        ? 'calc(10px + env(safe-area-inset-top, 0px)) calc(12px + env(safe-area-inset-right, 0px)) 6px calc(12px + env(safe-area-inset-left, 0px))'
                        : 'calc(10px + env(safe-area-inset-top, 0px)) calc(12px + env(safe-area-inset-right, 0px)) 10px calc(12px + env(safe-area-inset-left, 0px))',
                    minHeight: 56,
                    backgroundColor: 'background.paper',
                    borderBottom: isRoot ? 0 : '1px solid',
                    borderColor: 'divider',
                }}
            >
                {isRoot ? (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minHeight: 40 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }} />
                            {showAiAction && (
                                <Box component="button" type="button" aria-label="Assistente" onClick={() => navigate('/chat')} sx={appBarButtonSx}>
                                    <Sparkles size={20} />
                                </Box>
                            )}
                            {pathname === '/more' && (
                                <Box component="button" type="button" aria-label="Aparência" onClick={openThemeSheet} sx={appBarButtonSx}>
                                    <Moon size={20} />
                                </Box>
                            )}
                            {appBarAction && <AppBarActionButton {...appBarAction} />}
                        </Box>
                        {pathname === '/more' ? (
                            <MobileMoreHeader />
                        ) : pathname === '/home' ? (
                            // Home: marca (selo gradiente + logo oficial) e nome no lugar do título "Início".
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, p: '2px 4px 6px' }}>
                                <Box component="span" sx={{ flexShrink: 0, width: 34, height: 34, borderRadius: '10px', backgroundImage: theme.palette.gradient.brand, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box component="img" src={logoMark} alt="" sx={{ width: 22, height: 22, display: 'block', objectFit: 'contain' }} />
                                </Box>
                                <Typography component="h1" sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                                    Flow Vendas
                                </Typography>
                            </Box>
                        ) : (
                            <Typography component="h1" sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.03em', p: '2px 4px 6px', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {title}
                            </Typography>
                        )}
                    </>
                ) : (
                    <>
                        <Box component="button" type="button" onClick={handleBack} aria-label="Voltar" sx={appBarButtonSx}>
                            <ArrowLeft size={20} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography component="h1" sx={{ fontSize: 17, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {title}
                            </Typography>
                            {subtitle && <Box sx={{ fontSize: 11.5, color: 'text.disabled', mt: '1px', lineHeight: 1.4 }}>{subtitle}</Box>}
                        </Box>
                        {appBarAction && <AppBarActionButton {...appBarAction} />}
                    </>
                )}
            </Box>

            <Box
                component="main"
                sx={{
                    flex: 1,
                    minWidth: 0,
                    width: '100%',
                    boxSizing: 'border-box',
                    p: '16px',
                    pl: 'calc(16px + env(safe-area-inset-left, 0px))',
                    pr: 'calc(16px + env(safe-area-inset-right, 0px))',
                    pb: hideBottomNav
                        ? 'calc(28px + env(safe-area-inset-bottom, 0px))'
                        : `calc(${TABBAR_HEIGHT}px + 28px + env(safe-area-inset-bottom, 0px))`,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowX: 'hidden',
                    // `hidden` faz do main um contêiner de rolagem e quebra o `position: sticky`
                    // dos filhos (topo fixo das listagens); `clip` corta sem criar contêiner.
                    '@supports (overflow-x: clip)': { overflowX: 'clip' },
                }}
            >
                <AppBarActionsProvider value={setAppBarAction}>
                    <AppBarSubtitleProvider value={setSubtitle}>
                        <AppBarTitleProvider value={setTitleOverride}>
                            <ErrorBoundary key={pathname}>
                                <Outlet />
                            </ErrorBoundary>
                        </AppBarTitleProvider>
                    </AppBarSubtitleProvider>
                </AppBarActionsProvider>
            </Box>

            {/* Barra inferior (`.tabbar`): Início · Ordens · (+) · Orçamentos · Mais */}
            {!hideBottomNav && (
                <Box
                    component="nav"
                    aria-label="Navegação principal"
                    sx={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: theme.zIndex.appBar,
                        height: `calc(${TABBAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
                        boxSizing: 'border-box',
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 74px 1fr 1fr',
                        alignItems: 'end',
                        p: '6px 6px calc(8px + env(safe-area-inset-bottom, 0px))',
                        backgroundColor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    {tabs.filter((t) => t.id === 'home' || t.id === 'orders').map((tab) => renderTab({ ...tab, active: tab.id === activeTabId }))}
                    {quickActions.length > 0 ? (
                        <Box
                            component="button"
                            type="button"
                            aria-label="Novo"
                            onClick={() => { if (!quickPress.consumeLongPress()) setQuickOpen(true); }}
                            {...quickPress.handlers}
                            sx={{
                                display: 'flex', justifyContent: 'center', transform: 'translateY(-14px)', border: 0, background: 'transparent', p: 0, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                                // long-press: sem seleção de texto, callout/menu nativo (iOS) e zoom por duplo toque
                                userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', touchAction: 'manipulation',
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: 54, height: 54, borderRadius: '50%', backgroundImage: theme.palette.gradient.brand, color: '#fff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 8px 20px rgba(37,99,235,.45)', border: '4px solid', borderColor: 'background.default', boxSizing: 'border-box',
                                }}
                            >
                                <Plus size={26} />
                            </Box>
                        </Box>
                    ) : <Box />}
                    {tabs.filter((t) => t.id === 'quotes').map((tab) => renderTab({ ...tab, active: tab.id === activeTabId }))}
                    {renderTab({ id: 'more', label: 'Mais', path: '/more', icon: LayoutGrid, active: moreActive })}
                </Box>
            )}

            <MobileQuickActions open={quickOpen} onClose={() => setQuickOpen(false)} actions={quickPlus.visible} />
            <MobileQuickActionsEdit
                open={quickEditOpen}
                onClose={() => setQuickEditOpen(false)}
                available={quickActions}
                selectedIds={quickPlus.selectedIds}
                defaultIds={quickPlus.defaultIds}
                onSave={quickPlus.save}
            />

            <MobileSheet
                open={themeOpen}
                onClose={() => setThemeOpen(false)}
                title="Aparência"
                footer={<KitButton variant="primary" sx={{ flex: 1 }} onClick={applyTheme}>Aplicar</KitButton>}
            >
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <ThemeOption active={themeChoice === 'light'} icon={Sun} label="Claro" hint="Padrão" onClick={() => setThemeChoice('light')} />
                    <ThemeOption active={themeChoice === 'dark'} icon={Moon} label="Escuro" hint="Descansa a vista" onClick={() => setThemeChoice('dark')} />
                </Box>
            </MobileSheet>
        </Box>
    );
}
